import type { NotifyKind } from '@safetyhub/shared';

/**
 * 통지 채널 **종류 축** 헬퍼 (출처 축 제거 + 인코딩 단일화, 2026-08-23).
 *
 * F-C/F-D가 도입했던 출처 축(`{kind, source}`)은 저장·표시·발송 전 구간 누수로 web에서
 * 비활성화됐다가, `member.email` 재정의로 이메일 개념이 하나로 수렴하며 타입에서 통째로
 * 제거됐다(결정 기록 `2026-08-23-contact-email-redefine-and-source-axis-removal.md`).
 * 이어 객체 인코딩(`notifyChannels: {kind}[]`)도 저장 실측 0건으로 접혀, 정본은
 * **`notifyVia: NotifyKind[]`**(@safetyhub/shared) 하나다. 발송 주소는 서버 폴백
 * 사다리(`member.email → 계정 이메일` — 구 프로필 칸은 E4에서 삭제)가 정하고, 화면·저장은 종류만 다룬다.
 */

export interface ChannelContact {
  phone?: string | null;
  /** 연락 이메일(`Member.email`) */
  email?: string | null;
  /** 계정의 로그인 이메일(`user_auths` provider=EMAIL) */
  accountEmail?: string | null;
}

const trimmed = (v?: string | null): string | null => (v && v.trim() ? v.trim() : null);

/** 정본 타입은 @safetyhub/shared — 기존 import 경로 호환을 위해 재노출한다. */
export type { NotifyKind };

/** 담당자가 실제로 보유한 채널 **종류** 목록. */
function availableNotifyKinds(contact: ChannelContact | undefined | null): NotifyKind[] {
  if (!contact) return [];
  const kinds: NotifyKind[] = [];
  if (trimmed(contact.phone)) kinds.push('sms');
  // 계정 로그인 이메일도 이메일 종류의 근거다 — 서버 발송 사다리의 마지막 칸이
  // accountEmail이라(resolveApproverContact) 종류만 있어도 실제로 도달한다.
  if (trimmed(contact.email) || trimmed(contact.accountEmail)) kinds.push('email');
  return kinds;
}

/**
 * 담당자가 바뀌었을 때의 **기본 선택**(종류 축): 보유 채널 전부.
 *
 * 구 `getDefaultNotifyVia`와 같은 규칙이되, **계정 로그인 이메일도 이메일 채널의 근거**로
 * 인정한다(위 `availableNotifyKinds` 참조).
 *
 * ⚠️ 보유 채널이 없으면 `[]`가 아니라 **`undefined`**를 돌려준다. 서버 계약에서 `[]`는
 * "전부 금지"고(`allowsNotifyKind`), `undefined`가 "제한 없음"이다 — 연락처 없는 담당자에게
 * `[]`를 시드하면 나중에 연락처가 생겨도 통지가 영구 차단된다(F-D 결함 A4의 정체).
 */
export function defaultNotifyVia(contact: ChannelContact | undefined | null): NotifyKind[] | undefined {
  const kinds = availableNotifyKinds(contact);
  return kinds.length > 0 ? kinds : undefined;
}

/**
 * 이 행에서 현재 선택된 채널 **종류**.
 *
 * 인코딩 단일화(2026-08-23) 이후 클라이언트가 다루는 행에는 정본 축(`notifyVia`)만 있다 —
 * 서버 응답은 notifyVia만 내려주고, 위임 기본값 복원도 경계(delegationDefaults)에서
 * notifyVia로 정규화한다. 구 객체 축(`notifyChannels`) 우선 분기는 그래서 접었다
 * (서버 `notify-via.util` pick()의 잔존 흡수와 달리 클라에는 그 축의 유입 경로가 없다).
 */
export function selectedNotifyKinds(line: {
  notifyVia?: NotifyKind[];
}): NotifyKind[] | undefined {
  return line.notifyVia;
}
